import { type ReactNode } from "react";

import { cn } from "@/shared/lib";

interface ItemListProps<T> {
  /** DDC: 제네릭 데이터 컬렉션 */
  data: readonly T[];
  /** 각 아이템의 렌더링 위임 */
  renderItem: (item: T) => ReactNode;
  /** 각 아이템의 고유 키 추출 */
  keyExtractor: (item: T) => string;
  /** 아이템 클릭 핸들러 */
  onSelect?: (item: T) => void;
  className?: string;
}

/**
 * 제네릭 드롭다운 리스트 (DDC Feature UI)
 *
 * 도메인 무관한 리스트 렌더링을 담당한다.
 * 글라스모피즘 스타일 + 접근성(listbox/option) 지원.
 */
export function ItemList<T>({
  data,
  renderItem,
  keyExtractor,
  onSelect,
  className,
}: ItemListProps<T>) {
  return (
    <ul
      role="listbox"
      className={cn(
        "absolute end-0 top-full mt-2 min-w-[180px] overflow-hidden",
        "rounded-2xl border border-white/40 bg-white/80 backdrop-blur-xl",
        "shadow-[0_8px_32px_rgba(123,108,246,0.2)]",
        "animate-in fade-in slide-in-from-top-2 duration-200",
        "py-1.5",
        className
      )}
    >
      {data.map((item) => (
        <li
          key={keyExtractor(item)}
          role="option"
          className="cursor-pointer"
          onClick={() => onSelect?.(item)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect?.(item);
            }
          }}
          tabIndex={0}
        >
          {renderItem(item)}
        </li>
      ))}
    </ul>
  );
}
